import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Header,
  HeaderBackButton,
  HeaderCopyButton,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/app/ui/components/breadcrumb';
import { HugeiconsIcon } from '@hugeicons/react';
import { AiBrain02Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { MainScrollArea, MainHeader } from '@/app/ui/components/main';
import { Button } from '@/app/ui/components/button';
import { H1 } from '@/app/ui/components/heading';
import OrganizationKnowledgeBases from '@/app/components/features/organizations/OrganizationKnowledgeBases';
import { createKnowledgeBaseOpenAtom } from '@/app/components/features/organizations/knowledgeBase/store';
import { useAtom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { CreateKnowledgeBase } from '@/app/components/features/organizations/CreateKnowledgeBase';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/app/ui/components/tabs';
import { OrganizationTenderFeedbacks } from '@/app/components/features/organizations/OrganizationTenderFeedbacks';
import { useNavigate } from '@tanstack/react-router';

const tabs = {
  INTERNAL: 'internal',
  TENDER_FEEDBACKS: 'tender_feedbacks',
} as const;

type TabValue = (typeof tabs)[keyof typeof tabs];

const knowledgeBaseTabAtom = atomWithStorage<TabValue>('kb-tab', tabs.INTERNAL);

export const Route = createFileRoute('/_authed/(organization)/_layout/knowledge-base/')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('components.internalKnowledgeBase');
  const tPage = useTranslations('pages.knowledgeBase');
  const setCreateKnowledgeBaseOpen = useSetAtom(createKnowledgeBaseOpenAtom);
  const [tab, setTab] = useAtom(knowledgeBaseTabAtom);
  const navigate = useNavigate();

  return (
    <>
      <CreateKnowledgeBase />
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={AiBrain02Icon} />
                {t('heading')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
        {/* <HeaderButton
          aria-label={t('buttons.addNew')}
          onClick={() => setCreateKnowledgeBaseOpen(true)}
        >
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton> */}
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader className="max-md:flex">
          <H1 className="max-md:hidden">{t('heading')}</H1>
          {tab === tabs.INTERNAL ? (
            <Button
              variant="secondary"
              className="max-md:w-full"
              kind="with-icon"
              onClick={() => setCreateKnowledgeBaseOpen(true)}
            >
              <HugeiconsIcon icon={PlusSignIcon} />
              {tPage('button1')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="max-md:w-full"
              kind="with-icon"
              onClick={() => navigate({ to: '/knowledge-base/new-feedback' })}
            >
              <HugeiconsIcon icon={PlusSignIcon} />
              {tPage('button2')}
            </Button>
          )}
        </MainHeader>
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as TabValue)}
          className="flex flex-col grow min-h-0 pt-2"
        >
          <TabsList className={'px-x'}>
            <TabsTab value={tabs.INTERNAL}>{tPage('tab1')}</TabsTab>
            <TabsTab value={tabs.TENDER_FEEDBACKS}>{tPage('tab2')}</TabsTab>
          </TabsList>
          <TabsPanel value={tabs.INTERNAL}>
            <OrganizationKnowledgeBases />
          </TabsPanel>
          <TabsPanel value={tabs.TENDER_FEEDBACKS}>
            <OrganizationTenderFeedbacks />
          </TabsPanel>
        </Tabs>
      </MainScrollArea>
    </>
  );
}
